import { apiErrorMessage } from '@agos/api-client'
import { useGetEmployeesIdWaiterWallet } from '@agos/api-client/hr'
import { postRevenueHandovers } from '@agos/api-client/payments'
import { cardPayOnlineLabel, centsToEuros, formatCents, qrPayLabel, T, toDateParam, tt, useAuth, useModalFocus } from '@agos/shared'
import { BUTTON_SECONDARY, cn } from '@agos/ui'
import { handoverRegisterNote, handoverScopeNote, handoverTabNote } from './handover-scope'
import {
  Banknote,
  Calculator,
  CreditCard,
  Globe,
  Loader2,
  Plus,
  QrCode,
  Receipt,
  ShoppingBag,
  Ticket,
  Trash2,
  Wallet,
  X,
} from 'lucide-react'
import { useRef, useState } from 'react'

import {
  handoverTotals,
  documentedInWallet,
  documentedNote,
  documentedSplit,
  unpaidInWallet,
  unpaidNote,
  type WalletOrder,
  joinLineNotes,
  type HandoverLine,
  type WaiterWalletSummary,
} from './handover-math'

/**
 * Closing a shift, ported from legacy `components/waiter-handover-modal.tsx`.
 *
 * The number at the bottom is counted against physical banknotes, so the
 * arithmetic behind it lives in `handover-math.ts` with tests. What is here is
 * the form: three lists a waiter adds rows to, and one figure that follows.
 *
 * No fuel section, unlike the driver's version — a waiter refuels nothing.
 */

const FIELD_CLASSES =
  'px-2.5 py-1.5 rounded-lg bg-background border border-border text-sm outline-none focus:ring-2 focus:ring-primary/20'

let rowCounter = 0
function newRow(): HandoverLine {
  rowCounter += 1
  return { id: `row-${rowCounter}`, amount: '', note: '' }
}

export function WaiterHandoverModal({
  waiterId,
  waiterName,
  onClose,
  onSubmitted,
}: {
  waiterId: number
  waiterName: string
  onClose: () => void
  onSubmitted?: () => void
}) {
  const [vouchers, setVouchers] = useState<HandoverLine[]>([])
  const [purchases, setPurchases] = useState<HandoverLine[]>([])
  const [payouts, setPayouts] = useState<HandoverLine[]>([])
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const today = toDateParam(new Date())
  const {
    data: wallet,
    isLoading,
    isError,
  } = useGetEmployeesIdWaiterWallet(waiterId, { dateFrom: today, dateTo: today })

  const summary = (wallet as { summary?: WaiterWalletSummary } | undefined)?.summary ?? null
  const totals = handoverTotals({ summary, vouchers, purchases, payouts })
  /* §9.466 - **the answer to the owner's 19,80 €.** The wallet has always answered the whole `orders`
   * list and this screen read only `summary`, so the takings could include a table nobody had paid for
   * with nothing on the screen saying so. Said, never subtracted: what a shift owes is the owner's
   * decision, and `TODO.md` #18 carries it costed. */
  const walletOrders = Array.isArray((wallet as { orders?: unknown } | undefined)?.orders)
    ? ((wallet as { orders: WalletOrder[] }).orders)
    : []
  const unpaid = unpaidInWallet(walletOrders)
  const documented = documentedInWallet(walletOrders)
  const split = documentedSplit(walletOrders)
  const { hasPermission } = useAuth()
  const maySeeFiskal = hasPermission('fiskal') || hasPermission('fiskal_reconcile')

  const submit = async () => {
    setIsSubmitting(true)
    setError(null)
    try {
      await postRevenueHandovers({
        totalRevenue: centsToEuros(totals.totalRevenueCents),
        cardPayAmount: centsToEuros(totals.cardOnlineCents),
        cardAmount: centsToEuros(totals.cardTerminalCents),
        /* §9.456 - **the field this payload deducted and never sent.**
         *
         * `handoverTotals` puts `cardCourierCents` into `deductionCents`, so `cashToHandOver` below
         * is right - and without this line the stored deductions summed to **less** than the cash
         * the same submission said was owed, by exactly the courier's card. The audit then reported
         * a card drift that was a missing field rather than missing money.
         *
         * `handoverPayload` on the driving side has sent all thirteen fields since FB-068; this one
         * sent nine. Two payload builders for one endpoint, a third time. */
        cardCourierAmount: centsToEuros(totals.cardCourierCents),
        vouchersAmount: centsToEuros(totals.voucherCents),
        fuelAmount: 0,
        purchase1Amount: centsToEuros(totals.purchaseCents),
        purchase2Amount: 0,
        payout1Amount: centsToEuros(totals.payoutCents),
        // The schema has two fixed slots, not a list, so several typed rows
        // collapse into the first with their notes joined. The detail survives
        // as text rather than being lost.
        payout1Note: joinLineNotes(payouts, formatCents) || undefined,
        payout2Amount: 0,
        cashToHandOver: centsToEuros(totals.cashToHandOverCents),
        orderCount: summary?.orderCount ?? 0,
        /* §9.456 - **the kasa's own side of the comparison, also never sent.**
         *
         * These four are what `HandoverReconciliation` checks the entered figures against. Absent,
         * they store as nothing and read as **0** - which is exactly the sentence the owner asked
         * about, *"Kasa zaznamenala na kartách 0,00 €"*: the audit faithfully reporting a row it
         * was never given the wallet's side of. */
        cashTotal: centsToEuros(totals.cashCents),
        cardTerminalTotal: centsToEuros(totals.cardTerminalCents),
        cardCourierTotal: centsToEuros(totals.cardCourierCents),
        cardOnlineTotal: centsToEuros(totals.cardOnlineCents),
        /* §9.741, owner O112 - the figure `cashToHandOver` above already has off. The server recomputes it
         * from the orders when it can and stores what was sent when it cannot, and an app that sends none
         * is recorded as one that predates QR Pay (§10A.390) - which this one no longer is. */
        qrPayTotal: centsToEuros(totals.qrPayCents),
        // Always sent, for the reason `handoverPayload` gives: without it the server assumes today,
        // and a shift settled at 01:00 is yesterday's.
        forDate: today,
      })
      onSubmitted?.()
      onClose()
    } catch (caught) {
      setError(apiErrorMessage(caught, tt('Chyba pri ukladaní')))
    } finally {
      setIsSubmitting(false)
    }
  }

  /* §9.477 - §9.441's three keyboard behaviours, which this overlay's `aria-modal` promised and did
     not keep. Rendered only when open, so `open` is a constant `true` here. */
  const panelRef = useRef<HTMLDivElement>(null)
  const { onKeyDown } = useModalFocus(true, panelRef, isSubmitting ? null : onClose)

  return (
    /* §9.469 - a shift's whole takings are handed over here and the dialog did not say it was one. */
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="waiter-handover-title"
      className="fixed inset-0 bg-black/70 backdrop-blur-sm z-50 flex items-center justify-center p-4"
    >
      {/* §9.477 - the card rather than the scrim above it, which has no focusable children. */}
      <div
        ref={panelRef}
        onKeyDown={onKeyDown}
        className="bg-card border border-border rounded-2xl w-full max-w-lg max-h-[92vh] overflow-hidden flex flex-col shadow-2xl"
      >
        <div className="flex items-center gap-3 px-5 py-4 border-b border-border flex-shrink-0">
          <Calculator className="w-5 h-5 text-primary" />
          <div className="flex-1 min-w-0">
            {/* An `<h2>` rather than a `<p>`: `aria-labelledby` naming a paragraph is a name with no
             * heading behind it. The classes are unchanged, so nothing on the screen moves. */}
            <h2 id="waiter-handover-title" className="font-bold text-foreground">
              <T>Odovzdať tržbu</T>
            </h2>
            <p className="text-xs text-muted-foreground truncate">
              {waiterName} · {tt('dnes')}
            </p>
          </div>
          <button
            type="button"
            aria-label={tt('Zatvoriť')}
            onClick={onClose}
            /* §9.477 - where the opening focus lands when nothing above it is a field. */
            data-dialog-cancel
            className="p-1.5 rounded-lg hover:bg-muted transition-colors"
          >
            <X className="w-4 h-4 text-muted-foreground" />
          </button>
        </div>

        {isLoading ? (
          <div className="flex-1 flex items-center justify-center py-12 text-muted-foreground text-sm gap-2">
            <Loader2 className="w-4 h-4 animate-spin" /> <T>Načítavam dnešné tržby…</T>
          </div>
        ) : (
          <div className="flex-1 overflow-y-auto px-5 py-4 space-y-4">
            {isError && (
              <p className="text-sm text-red-500">
                <T>Nepodarilo sa načítať dnešnú tržbu</T>
              </p>
            )}

            {/* §9.449, FB-771 - what this figure counts, before it is compared with a register's
             * closing slip. The owner's two totals differed by 111,05 and neither was wrong. */}
            <div
              className="rounded-xl border border-border/60 bg-muted/30 px-3 py-2 space-y-1 text-xs text-muted-foreground"
              data-handover-scope
            >
              <p className="font-semibold text-foreground">{handoverScopeNote()}</p>
              <p>{handoverTabNote()}</p>
              <p data-handover-register-note>{handoverRegisterNote()}</p>
            </div>

            <div className="bg-muted/50 rounded-xl p-3 space-y-1.5 text-sm">
              <Row
                icon={Wallet}
                label={tt('Celková tržba')}
                value={formatCents(totals.totalRevenueCents)}
                bold
              />
              <Row
                icon={Banknote}
                label={tt('Hotovosť')}
                value={formatCents(totals.cashTakingsCents)}
                marker="cash"
              />
              <Row
                icon={CreditCard}
                label={tt('Karta terminál')}
                value={`−${formatCents(totals.cardTerminalCents)}`}
              />
              <Row
                icon={CreditCard}
                label={tt('Karta kuriér')}
                value={`−${formatCents(totals.cardCourierCents)}`}
              />
              {/* §9.767, owner O129 - *"also modals for them (čašník & rozvozár app)"*: the online card's one
                * name (it said *Online*) and QR Pay always the next line, 0,00 € included. Both are off the
                * cash in `handoverTotals`. */}
              <Row
                icon={Globe}
                label={cardPayOnlineLabel()}
                value={`−${formatCents(totals.cardOnlineCents)}`}
                marker="cardpay-online"
              />
              <Row
                icon={QrCode}
                label={qrPayLabel()}
                value={`−${formatCents(totals.qrPayCents)}`}
                marker="qr"
              />
              <p className="text-[11px] text-muted-foreground pt-1">
                {summary?.orderCount ?? 0} <T>objednávok</T>
              </p>
              {/* §9.466 - drawn only when there is something to say, in amber rather than muted grey:
                * this is the one line on the dialog that explains a shortfall, and a note nobody
                * notices is the same as no note. */}
              {/* §9.571 - the owner's instruction: *Fiskal Card* and *Fiskal Hotovosť*, "visible only
                * with permissions... admin and main čašníčka". One key for this and the archive warning,
                * because they answer the same question; the head waitress gets it as an override. */}
              {maySeeFiskal && (
                <>
                  <p
                    className="text-[11px] font-semibold text-sky-700 dark:text-sky-300"
                    data-handover-documented={String(documented.cents)}
                  >
                    {documentedNote(documented, totals.totalRevenueCents, walletOrders.length)}
                  </p>
                  {/* §9.577, FB-879 - **two noughts are not an answer when nobody was asked.** The
                    * stamps this split reads only reach the wallet from §10A.293 (2026-09-27 23:55),
                    * one commit before §9.568 built the figure - so an older server sends neither key
                    * and `hasLiveFiskalDocument` reads that exactly like an unprinted order. The owner
                    * read *Fiskal hotovosť 0, Fiskal karta 0* on receipts they had watched print. */}
                  {split.unknown ? (
                    <p
                      className="text-[11px] font-semibold text-amber-600 dark:text-amber-400"
                      data-handover-fiskal-unknown
                    >
                      <T>
                        Server nevie povedať, koľko z tejto sumy má fiškálny doklad - odpoveď
                        neobsahuje údaje o dokladoch.
                      </T>
                    </p>
                  ) : (
                    <div
                      className="flex items-center gap-3 text-[11px] font-semibold"
                      data-handover-fiskal-split
                    >
                      <span className="text-emerald-700 dark:text-emerald-400" data-fiskal-cash={String(split.cashCents)}>
                        <T>Fiskal Hotovosť</T> {formatCents(split.cashCents)}
                      </span>
                      <span className="text-sky-700 dark:text-sky-300" data-fiskal-card={String(split.cardCents)}>
                        <T>Fiskal Karta</T> {formatCents(split.cardCents)}
                      </span>
                    </div>
                  )}
                </>
              )}
              {unpaidNote(unpaid) !== '' && (
                <p className="text-[11px] font-semibold text-amber-600 dark:text-amber-400" data-handover-unpaid={String(unpaid.cents)}>
                  {unpaidNote(unpaid)}
                </p>
              )}
            </div>

            <LineSection
              icon={Ticket}
              title={tt('Stravné lístky')}
              lines={vouchers}
              onChange={setVouchers}
              totalCents={totals.voucherCents}
            />
            <LineSection
              icon={ShoppingBag}
              title={tt('Nákupy')}
              lines={purchases}
              onChange={setPurchases}
              totalCents={totals.purchaseCents}
            />
            <LineSection
              icon={Receipt}
              title={tt('Výdavky')}
              lines={payouts}
              onChange={setPayouts}
              totalCents={totals.payoutCents}
            />

            <div
              className={cn(
                'rounded-xl p-3 flex items-center justify-between border',
                totals.cashToHandOverCents < 0
                  ? 'bg-red-500/10 border-red-500/30'
                  : 'bg-emerald-500/10 border-emerald-500/30',
              )}
            >
              <span className="font-bold text-sm">
                <T>Hotovosť na odovzdanie</T>
              </span>
              <span
                className={cn(
                  'text-xl font-black tabular-nums',
                  totals.cashToHandOverCents < 0 ? 'text-red-500' : 'text-emerald-600',
                )}
              >
                {formatCents(totals.cashToHandOverCents)}
              </span>
            </div>

            {totals.cashToHandOverCents < 0 && (
              <p className="text-[11px] text-red-500">
                {/* Not clamped at zero: a waiter who paid out more than they
                    took is owed money, and hiding that from the person counting
                    the safe is how it goes unpaid. */}
                <T>Výdavky prevyšujú tržbu - reštaurácia dlhuje obsluhe.</T>
              </p>
            )}

            {error && <p className="text-sm text-red-500">{error}</p>}
          </div>
        )}

        <div className="flex gap-3 px-5 py-4 border-t border-border flex-shrink-0">
          <button
            type="button"
            onClick={onClose}
            className={cn(BUTTON_SECONDARY, 'flex-1 py-2.5 rounded-xl text-sm')}
          >
            <T>Zrušiť</T>
          </button>
          <button
            type="button"
            onClick={() => void submit()}
            disabled={isSubmitting || isLoading}
            className="flex-1 flex items-center justify-center gap-2 py-2.5 rounded-xl bg-emerald-500 text-white text-sm font-bold hover:bg-emerald-400 transition-colors disabled:opacity-50"
          >
            {isSubmitting ? (
              <Loader2 className="w-4 h-4 animate-spin" />
            ) : (
              <Calculator className="w-4 h-4" />
            )}
            <T>Odovzdať</T>
          </button>
        </div>
      </div>
    </div>
  )
}

function Row({
  icon: Icon,
  label,
  value,
  bold,
  marker,
}: {
  icon: typeof Wallet
  label: string
  value: string
  bold?: boolean
  /**
   * §9.741, §9.767 - the cash QR Pay is taken off, its own row and the CardPay online row it sits beside, so a
   * test can find them.
   */
  marker?: string
}) {
  return (
    <div
      className={cn(
        'flex items-center justify-between',
        bold ? 'font-bold text-foreground' : 'text-muted-foreground',
      )}
      data-handover-row={marker}
    >
      <span className="flex items-center gap-1.5">
        <Icon className="w-3.5 h-3.5" /> {label}
      </span>
      <span className="tabular-nums">{value}</span>
    </div>
  )
}

function LineSection({
  icon: Icon,
  title,
  lines,
  onChange,
  totalCents,
}: {
  icon: typeof Ticket
  title: string
  lines: HandoverLine[]
  onChange: (lines: HandoverLine[]) => void
  totalCents: number
}) {
  return (
    <div className="space-y-1.5">
      <div className="flex items-center justify-between">
        <p className="text-xs font-bold text-muted-foreground uppercase tracking-wider flex items-center gap-1.5">
          <Icon className="w-3.5 h-3.5" /> {title}
        </p>
        <span className="text-xs font-bold tabular-nums">{formatCents(totalCents)}</span>
      </div>

      {lines.map((line, index) => (
        <div key={line.id} className="flex gap-1.5">
          <input
            value={line.amount}
            onChange={(event) =>
              onChange(
                lines.map((candidate, candidateIndex) =>
                  candidateIndex === index
                    ? { ...candidate, amount: event.target.value }
                    : candidate,
                ),
              )
            }
            inputMode="decimal"
            placeholder="0,00"
            aria-label={`${title} - ${tt('suma')}`}
            className={cn(FIELD_CLASSES, 'w-24 tabular-nums')}
          />
          <input
            value={line.note}
            onChange={(event) =>
              onChange(
                lines.map((candidate, candidateIndex) =>
                  candidateIndex === index ? { ...candidate, note: event.target.value } : candidate,
                ),
              )
            }
            placeholder={tt('poznámka')}
            aria-label={`${title} - ${tt('poznámka')}`}
            className={cn(FIELD_CLASSES, 'flex-1')}
          />
          <button
            type="button"
            aria-label={tt('Odstrániť riadok')}
            onClick={() =>
              onChange(lines.filter((_candidate, candidateIndex) => candidateIndex !== index))
            }
            className="px-2 rounded-lg text-muted-foreground hover:text-red-500 transition-colors"
          >
            <Trash2 className="w-3.5 h-3.5" />
          </button>
        </div>
      ))}

      <button
        type="button"
        onClick={() => onChange([...lines, newRow()])}
        className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg border border-dashed border-border text-xs font-semibold text-muted-foreground hover:text-foreground hover:border-muted-foreground transition-colors"
      >
        <Plus className="w-3 h-3" /> <T>Pridať riadok</T>
      </button>
    </div>
  )
}
